import { useEffect, useState } from 'react';
import { getFareEstimate } from '@rideiq/api/services/Rides';
import { useRideEditor } from '@rideiq/rides/hooks/useRideEditor';
import { uniqBy } from 'lodash';

import { TypeFareEstimate, useRideOverviewStore } from '../stores/rideOverviewStore';

type FareEstimateRequestForSavedRide = {
  tripSlug: string;
};

const useRideOverviewDetails = () => {
  const { rideDetails, getRiderOptions } = useRideEditor();
  const { ridersOnRide } = getRiderOptions();

  const allServices = ridersOnRide.flatMap((rider) => rider.services ?? []);
  const uniqueServicesForTrip = uniqBy(allServices, 'code');

  const isEditView = window.location.pathname.match(/(new|edit|duplicate)/);

  const baseDetails = {
    rideId: rideDetails?.id,
    driverInfo: rideDetails?.driver || undefined,
  };

  const baseInfoDetails = rideDetails
    ? {
        createdAt: rideDetails.createdAt,
        updatedAt: rideDetails.updatedAt,
        status: rideDetails.status,
        cancellationReason: rideDetails.cancellationReason,
      }
    : {};

  if (isEditView) {
    return {
      ...baseDetails,
      boosterCount: ridersOnRide.reduce((count, rider) => (rider.booster ? (count += 1) : count), 0),
      riderCount: ridersOnRide.length,
      rideInfo: baseInfoDetails,
      services: uniqueServicesForTrip,
    };
  }
  const allRiders = rideDetails?.waypoints.reduce((riders, wp) => [...riders, ...wp.riders], []) || [];

  return {
    ...baseDetails,
    boosterCount: allRiders.reduce((count, rider) => (rider.needsBooster ? ++count : count), 0),
    riderCount: allRiders.length,
    rideInfo: baseInfoDetails,
    services: uniqueServicesForTrip,
  };
};

export function useRideOverview(): void {
  const { completeAnchorTime, waypointsOnMap, raWaypointOnMap, rideDetails, getRiderOptions } = useRideEditor();
  const { ridersOnRide } = getRiderOptions();
  const [fareEstimates, setFareEstimates] = useState<TypeFareEstimate>();
  const rideOverview = useRideOverviewDetails();
  const rideHistory = rideDetails?.rideHistory;
  const isEditView = window.location.pathname.match(/(new|edit|duplicate)/);

  const setRideOverview = useRideOverviewStore((state) => state.setRideOverview);
  const setRideHistory = useRideOverviewStore((state) => state.setRideHistory);
  const setRidersOnRide = useRideOverviewStore((state) => state.setRidersOnRide);
  const resetRideOverview = useRideOverviewStore((state) => state.resetRideOverview);

  const fetchFareEstimates = async (fareEstimateParams: FareEstimateForNewRide | FareEstimateRequestForSavedRide) => {
    const response = await getFareEstimate(fareEstimateParams);

    if (response?.type === 'success') {
      const fareEstimateData = response.data;

      const baseData = {
        duration: fareEstimateData.duration,
        distance: fareEstimateData.distance,
        disclaimerText: fareEstimateData.disclaimerText,
        errorText: '',
        perRiderFareDetails: fareEstimateData.perRiderFareDetails,
      };

      const showPerTripDetails = !fareEstimateData.perRiderFareDetails;

      if ('tripSlug' in fareEstimateParams) {
        setFareEstimates({
          ...baseData,
          ...(showPerTripDetails && {
            estimatedFare: fareEstimateData.billingInfo[0]?.display || '',
            fareDetails: fareEstimateData.pricingInfo.filter((f) => f.kind !== 'credit'),
            rideCredits: fareEstimateData.pricingInfo.filter((f) => f.kind === 'credit'),
            totalFareWithCredits: fareEstimateData.billingInfo.filter((bi) => bi.kind === 'total_with_ride_credits')[0]
              ?.display,
          }),
        });
      } else {
        setFareEstimates({
          ...baseData,
          ...(showPerTripDetails && {
            estimatedFare: fareEstimateData.estimatedNetFare || '',
            fareDetails: fareEstimateData?.fareDetails,
            rideCredits: [],
            totalFareWithCredits: undefined,
          }),
        });
      }
    }
  };

  useEffect(() => {
    if (isEditView) {
      if (waypointsOnMap.every((wp) => wp.location !== undefined)) {
        const haveWaypointsChanged = waypointsOnMap.some(
          (wp, i) => wp.location?.placeId !== rideDetails?.waypoints[i]?.location?.placeId
        );
        const anchor = rideDetails?.waypoints.find((wp) => wp.anchor);
        const hasAnchorTimeChanged = anchor
          ? completeAnchorTime?.getTime() !== anchor.estimatedArrivesAt?.getTime()
          : true;

        if (!haveWaypointsChanged && !hasAnchorTimeChanged && rideDetails?.slug) {
          fetchFareEstimates({ tripSlug: rideDetails.slug });
        } else {
          const validWaypoints = waypointsOnMap.filter((waypoint) => waypoint.latLng && waypoint.location);
          const validAnchor = waypointsOnMap.find((waypoint) => waypoint.anchor && waypoint.location);
          const raIndex = waypointsOnMap.findIndex(
            (waypoint) => waypoint.type === 'ra_start' || waypoint.type === 'ra_end'
          );

          if (validWaypoints.length < 2 || !validAnchor) {
            return;
          }

          if (raWaypointOnMap && raWaypointOnMap.location?.placeId && raIndex < 0) {
            if (raWaypointOnMap.type === 'ra_start') {
              validWaypoints.unshift(raWaypointOnMap);
            } else {
              validWaypoints.push(raWaypointOnMap);
            }
          }

          const fareEstimateParams = {
            waypoints: validWaypoints.map((wp) => ({
              anchor: wp.anchor,
              location: {
                address: wp.address || '',
                placeId: wp.location?.placeId || '',
              },
              passengerIds: wp.passengerIds || [],
            })),
            coordinates: validWaypoints.map((wp) => wp.latLng || ''),
            isPickup: waypointsOnMap[0]?.anchor,
            datetime: completeAnchorTime || new Date(),
          };

          fetchFareEstimates(fareEstimateParams);
        }
      } else if (waypointsOnMap.some((wp) => wp.location === undefined)) {
        setFareEstimates({
          ...fareEstimates,
          duration: fareEstimates && undefined,
          distance: fareEstimates && undefined,
          estimatedFare: fareEstimates && '$0.00',
          disclaimerText: '',
          errorText: waypointsOnMap.length > 2 ? 'Add all locations to get an updated estimate' : '',
        });
      }
    }
  }, [waypointsOnMap, raWaypointOnMap, completeAnchorTime?.getTime()]);

  useEffect(() => {
    if (!isEditView && rideDetails?.slug) {
      fetchFareEstimates({ tripSlug: rideDetails.slug });
    }
  }, [isEditView, rideDetails?.slug]);

  const mergeOverviewWithFareEstimates = rideOverview
    ? {
        rideId: rideOverview.rideId,
        riderCount: rideOverview.riderCount,
        boosterCount: rideOverview.boosterCount,
        rideInfo: {
          distance: fareEstimates?.distance,
          duration: fareEstimates?.duration,
          estimatedFare: fareEstimates?.estimatedFare,
          fareDetails: fareEstimates?.fareDetails,
          rideCredits: fareEstimates?.rideCredits,
          disclaimerText: fareEstimates?.disclaimerText || '',
          totalFareWithCredits: fareEstimates?.totalFareWithCredits,
          errorText: fareEstimates?.errorText || '',
          perRiderFareDetails: fareEstimates?.perRiderFareDetails,
          ...(rideOverview.rideInfo.createdAt && {
            createdAt: rideOverview.rideInfo.createdAt,
            updatedAt: rideOverview.rideInfo.updatedAt,
            status: rideOverview.rideInfo.status,
            cancellationReason: rideOverview.rideInfo.cancellationReason,
          }),
          riderServices: rideOverview.services,
        },
        rideDetails: rideDetails,
        driverInfo: rideOverview.driverInfo,
      }
    : {};

  useEffect(() => {
    setRideOverview(mergeOverviewWithFareEstimates);
  }, [fareEstimates, rideDetails, ridersOnRide, isEditView]);

  useEffect(() => {
    setRidersOnRide(ridersOnRide);
  }, [ridersOnRide]);

  useEffect(() => {
    setRideHistory(rideHistory);
  }, [rideHistory]);

  useEffect(() => {
    return () => resetRideOverview();
  }, []);
}
